<template>
  <div>
    <div class="zoom-actions">
      <v-btn @click="zoomIn">+</v-btn>
      <v-btn class="ml-2" @click="zoomOut">-</v-btn>
      <v-btn class="ml-2" @click="reset">
        <v-icon class="mr-1" color="icon" small>mdi-restart</v-icon>
      </v-btn>
    </div>

    <!---JSON code-->
    <div class="json-action">
      <v-btn @click="collapseJsonPreview">
        <v-icon> mdi-code-json </v-icon>
      </v-btn>
    </div>

    <div v-if="displayJsonPreview" class="json-preview">
      <div class="json-preview__header">
        <div v-if="jsonFormatError" class="d-flex align-center mb-3">
          <span class="json-preview__format-error">Invalid JSON format!</span>
          <v-tooltip top>
            <template #activator="{ on }">
              <v-btn v-on="on" @click="undoJson" class="ml-3">
                <v-icon>mdi-undo</v-icon>
              </v-btn>
            </template>
            <p>Undo to previous valid JSON state</p>
          </v-tooltip>
        </div>
      </div>
      <code-input
        :class="{ 'code-error': jsonFormatError }"
        :key="jsonInputKey"
        v-model="imageBannerDataJsonFormat"
        :title="''"></code-input>
    </div>
    <!---JSON code END -->

    <div class="panzoom-parent">
      <div ref="panzoom">
        <!--Image Banner settings -->
        <div class="d-flex justify-center">
          <div class="image-banner-data-wrapper">
            <div class="title mb-2">
              {{ $t("shortcodes.image_banners.banner_settings") }}
            </div>

            <div class="grid-layout-config">
              <div class="grid-layout-section">
                <h4 class="mb-3">Desktop Layout</h4>
                <va-select-input
                  class="mb-4"
                  v-model="imageBannerData.desktopGridLayout"
                  :choices="GRID_LAYOUT_OPTIONS"
                  :label="'Desktop Grid Layout'"
                  clearable></va-select-input>

                <va-text-input
                  v-if="imageBannerData.desktopGridLayout === 'custom'"
                  class="mb-4"
                  v-model="imageBannerData.desktopCustomLayout"
                  label="Custom Desktop Layout (e.g., 4x3)"
                  placeholder="4x3"></va-text-input>
              </div>

              <div class="grid-layout-section">
                <h4 class="mb-3">Mobile Layout</h4>
                <va-select-input
                  class="mb-4"
                  v-model="imageBannerData.mobileGridLayout"
                  :choices="GRID_LAYOUT_OPTIONS"
                  :label="'Mobile Grid Layout'"
                  clearable></va-select-input>

                <va-text-input
                  v-if="imageBannerData.mobileGridLayout === 'custom'"
                  class="mb-4"
                  v-model="imageBannerData.mobileCustomLayout"
                  label="Custom Mobile Layout (e.g., 2x3)"
                  placeholder="2x3"></va-text-input>
              </div>
            </div>
          </div>
        </div>
        <!--Image Banner settings END-->

        <!--Image Banners Configuration -->
        <div class="image-banners-container-wrapper">
          <div class="image-banners-container">
            <div
              v-for="(imageBanner, index) in imageBannerData.imageBanners"
              :key="`image-banner-${index}`"
              class="image-banner-item">
              <div class="image-banner-item__header">
                <div class="image-banner-item__title">Image Banner {{ index + 1 }}</div>
                <v-btn @click="removeImageBanner(index)" color="error" small>
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </div>

              <VaMediaObjectFileInput
                :label="`Image Banner ${index + 1} Image`"
                class="mb-4"
                :item-id="imageBanner.imageID"
                @update:photo="updateImageBannerImage(index, $event)" />

              <va-text-input
                class="mb-4"
                v-model="imageBanner.headline"
                label="Headline"
                placeholder="Enter image banner headline"></va-text-input>

              <va-text-input
                class="mb-4"
                v-model="imageBanner.subHeadline"
                label="Sub-headline"
                placeholder="Enter image banner sub-headline"></va-text-input>

              <va-text-input
                class="mb-4"
                v-model="imageBanner.url"
                label="Image Banner URL"
                placeholder="https://example.com"></va-text-input>
            </div>

            <div class="add-button-container">
              <v-btn :color="'primary'" @click="addImageBanner">Add Image Banner</v-btn>
            </div>
          </div>
        </div>

        <!--Image Banners Configuration END -->
      </div>
    </div>
  </div>
</template>

<script>
  import Panzoom from "@panzoom/panzoom";
  import CodeInput from "@/components/custom/CodeInput";
  import cloneDeep from "lodash/cloneDeep";
  import definedAbility from "@/ability";
  import { convertAssetsPathToAbsoluteLinks } from "@/utility/convertAssetsPaths";

  const GRID_LAYOUT_OPTIONS = [
    { text: "3×2 Grid (6 image banners)", value: "3x2" },
    { text: "3×3 Grid (9 image banners)", value: "3x3" },
    { text: "3×4 Grid (12 image banners)", value: "3x4" },
    { text: "Custom", value: "custom" },
  ];

  const DEFAULT_IMAGE_BANNER = {
    imageID: "",
    imageURL: "",
    headline: "",
    subHeadline: "",
    url: "",
  };

  export default {
    name: "ImageBannerDialog",
    components: { CodeInput },
    data() {
      return {
        GRID_LAYOUT_OPTIONS,
        ability: definedAbility(),
        panzoomInstance: null,
        imageBannerData: {
          desktopGridLayout: "3x2",
          desktopCustomLayout: "",
          mobileGridLayout: "1x1",
          mobileCustomLayout: "",
          imageBanners: [],
        },
        displayJsonPreview: false,
        jsonFormatError: false,
        paramData: null,
        jsonInputKey: 0,
      };
    },
    computed: {
      imageBannerDataJsonFormat: {
        get() {
          const displayData = this.imageBannerData;

          return JSON.stringify(displayData, null, " ");
        },
        set(value) {
          let parsedJson;
          try {
            this.jsonFormatError = false;
            parsedJson = JSON.parse(value);
          } catch (e) {
            this.jsonFormatError = true;
          }

          if (!this.jsonFormatError) {
            this.imageBannerData = parsedJson;
          }
        },
      },
    },
    watch: {
      imageBannerData: {
        handler(newValue) {
          this.updateValue(newValue);
        },
        deep: true,
      },
    },
    beforeMount() {
      this.setupReceiveDataListener();
      this.requestData();
    },
    mounted() {
      this.panzoomInstance = Panzoom(this.$refs.panzoom, {
        canvas: true,
        excludeClass: "panzoom-ignore",
        handleStartEvent: () => {},
      });

      this.$refs.panzoom && (this.$refs.panzoom.style.height = `${window.innerHeight - 24}px`);

      const parent = this.$refs.panzoom.parentElement;

      parent.addEventListener("wheel", event => {
        if (!event.shiftKey) {
          this.panzoomInstance.pan(-event.deltaX, -event.deltaY, { relative: true });

          return;
        }
        this.panzoomInstance.zoomWithWheel(event);
      });
    },
    methods: {
      addImageBanner() {
        const imageBanner = cloneDeep(DEFAULT_IMAGE_BANNER);
        this.imageBannerData.imageBanners.push(imageBanner);
      },
      removeImageBanner(index) {
        this.imageBannerData.imageBanners.splice(index, 1);
      },
      reset() {
        this.panzoomInstance.reset();
      },
      zoomIn() {
        this.panzoomInstance.zoomIn();
      },
      zoomOut() {
        this.panzoomInstance.zoomOut();
      },
      updateImageBannerImage(index, value) {
        if (value) {
          this.imageBannerData.imageBanners[index].imageID = value["@id"];

          if (this.ability.can("upload", "media object to inulti server")) {
            this.imageBannerData.imageBanners[index].imageURL = value.contentUrl;
          } else {
            this.imageBannerData.imageBanners[index].imageURL = value.contentUrl;
          }
        } else {
          this.imageBannerData.imageBanners[index].imageID = "";
          this.imageBannerData.imageBanners[index].imageURL = "";
        }
      },
      collapseJsonPreview() {
        this.displayJsonPreview = !this.displayJsonPreview;
      },
      undoJson() {
        const displayData = this.imageBannerData;
        this.imageBannerDataJsonFormat = JSON.stringify(displayData, null, " ");
        this.jsonInputKey++;
      },
      updateValue(val) {
        window.parent.postMessage(
          {
            mceAction: "updateImageBannerData",
            data: val,
          },
          origin,
        );
      },
      setupReceiveDataListener() {
        window.onmessage = message => {
          if (message.data.type === "sendData") {
            const { data } = message.data;

            // Process image banner image URLs to use backend API endpoint
            if (data.imageBanners) {
              data.imageBanners.forEach(imageBanner => {
                if (imageBanner.imageURL) {
                  imageBanner.imageURL = convertAssetsPathToAbsoluteLinks(imageBanner.imageURL);
                }
              });
            }

            this.imageBannerData = {
              ...data,
              imageBanners: data.imageBanners || [],
            };
          }
        };
      },
      requestData() {
        window.parent.postMessage(
          {
            mceAction: "requestData",
          },
          origin,
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/typography/index.scss";
  @import "@core/preset/preset/functions.scss";

  .zoom-actions {
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 100;
  }

  .json-action {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 102;
  }

  .title {
    color: get-color("subtitle");
    @include heading-4();
  }

  .image-banner-data-wrapper {
    border: 1px dashed get-color("primary");
    border-radius: 5px;
    background: get-color("element-bg-100");
    padding: 5px 10px;
    max-width: 600px;
    min-width: 600px;
  }

  .grid-layout-config {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 20px;

    .grid-layout-section {
      width: 100%;

      h4 {
        color: get-color("subtitle");
        @include heading-4();
        margin: 0;
      }
    }
  }

  .image-banners-container-wrapper {
    display: flex;
    direction: row;
    align-items: center;
    justify-content: center;
  }

  .image-banners-container {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    margin-top: 20px;
    width: 100%;
    max-width: 600px;
    min-width: 600px;
  }

  .image-banner-item {
    width: 100%;
    border: 1px dashed get-color("primary");
    border-radius: 5px;
    background: get-color("element-bg-100");
    padding: 15px;
    margin-bottom: 20px;

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;
    }

    &__title {
      font-weight: bold;
      color: get-color("subtitle");
      @include heading-4();
    }
  }

  .json-preview {
    position: absolute;
    top: 0;
    left: 5px;
    right: 5px;
    bottom: 5px;
    z-index: 101;
    background-color: get-color("element-bg-200");

    &__header {
      display: flex;
      background: get-color("element-bg-200");
      padding: 70px 20px 0;
    }

    &__format-error {
      color: get-color("error");
      @include text-body-1-bold();
    }
  }

  .code-error {
    border: 3px dashed get-color("error") !important;
  }

  .add-button-container {
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
  }
</style>
